/**
 * HelpLauncher — single global mount point for the help systems.
 *
 * This is the ONLY help element added to the app shell. It selects which of the
 * two distinct help systems is active for the current user/route (artist vs
 * gallery) and renders that system's button + drawer. For roles/pages with no
 * help system (admin, judge/curator, auth pages) it renders nothing.
 *
 * Keeping selection here means the app shell only ever gains one line, which
 * minimises merge surface with concurrent work on the headers.
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AuthService } from '../../../Services/auth.service';
import { HelpSystemBase } from '../../help-system-base';
import { ArtistHelpService } from '../../artist/artist-help.service';
import { GalleryHelpService } from '../../gallery/gallery-help.service';
import { HelpButtonComponent } from '../help-button/help-button.component';
import { HelpDrawerComponent } from '../help-drawer/help-drawer.component';

@Component({
  selector: 'app-help-launcher',
  standalone: true,
  imports: [CommonModule, HelpButtonComponent, HelpDrawerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (activeSystem; as sys) {
      <app-help-button [system]="sys"></app-help-button>
      <app-help-drawer [system]="sys"></app-help-drawer>
    }
  `,
})
export class HelpLauncherComponent implements OnInit, OnDestroy {
  activeSystem: HelpSystemBase | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private auth: AuthService,
    private router: Router,
    private artistHelp: ArtistHelpService,
    private galleryHelp: GalleryHelpService,
  ) {}

  ngOnInit(): void {
    this.resolveActiveSystem();
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => this.resolveActiveSystem());

    // Global keyboard shortcut: "?" toggles, Escape closes.
    document.addEventListener('keydown', this.onKeydown);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('keydown', this.onKeydown);
  }

  private resolveActiveSystem(): void {
    if (!this.auth.isAuthenticated()) { this.activeSystem = null; return; }

    // Two separate systems: pick by role. Gallery staff (incl. judge/curator)
    // and admins get no help PoC for now.
    if (this.auth.isArtist()) {
      this.activeSystem = this.artistHelp;
    } else if (this.auth.isGallery()) {
      this.activeSystem = this.galleryHelp;
    } else {
      this.activeSystem = null;
    }
  }

  private onKeydown = (e: KeyboardEvent): void => {
    if (!this.activeSystem) return;
    const target = document.activeElement;
    const typing = !!target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
    if (e.key === '?' && !typing) {
      e.preventDefault();
      this.activeSystem.toggle();
    } else if (e.key === 'Escape' && this.activeSystem.isOpen$.value) {
      this.activeSystem.close();
    }
  };
}
